import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * UI Primitives — "Paper-on-Black" design language.
 *
 * Signature:
 *   - 1px solid #000 border
 *   - Hard drop shadow `0 4px 0 0 #000` (or 2px for chips)
 *   - Press → `translateY(2px)` + shadow collapses to 2px
 *   - DM Sans display font, weights 400/600/700/900
 *   - Rounded pills for small things, 20px for cards/buttons
 *
 * Base utility classes are defined in globals.css (`paper-lift` etc.).
 */
import { forwardRef } from "react";
import { cx } from "./cx.js";
import { t } from "./locale.js";
// ─────────────────────────────────────────────────────────────
// character / tag palette — pastel 7-color wheel
// ─────────────────────────────────────────────────────────────
export const PASTEL_PALETTE = [
    "#FCA5A5", // rose
    "#93C5FD", // sky
    "#FDBA74", // amber
    "#86EFAC", // mint
    "#D6BBFB", // lilac
    "#7DD3FC", // cyan
    "#F9A8D4", // pink
];
/** Deterministic pastel for any character / tab key. */
export function pastelFor(key) {
    let hash = 0;
    for (let i = 0; i < key.length; i++)
        hash = (hash * 31 + key.charCodeAt(i)) | 0;
    return PASTEL_PALETTE[Math.abs(hash) % PASTEL_PALETTE.length];
}
const BTN_SIZE = {
    sm: "h-10 px-4 text-sm",
    md: "h-12 px-5 text-[15px]",
    lg: "h-14 px-6 text-base",
};
export const PaperButton = forwardRef(function PaperButton({ variant = "primary", size = "md", accent, leading, trailing, block, className, style, children, ...rest }, ref) {
    const base = cx("inline-flex items-center justify-center gap-2 rounded-[20px] font-bold tracking-tight select-none", block && "w-full", BTN_SIZE[size]);
    if (variant === "ghost") {
        return (_jsxs("button", { ref: ref, className: cx(base, "text-[var(--ink)] hover:bg-black/5 active:bg-black/10 transition-colors", "disabled:opacity-50 disabled:cursor-not-allowed", className), style: style, ...rest, children: [leading, children, trailing] }));
    }
    const bg = variant === "filled" ? accent ?? "var(--paper)" : "var(--paper)";
    return (_jsxs("button", { ref: ref, className: cx(base, "paper-lift paper-lift-interactive text-[var(--ink)]", className), style: { background: bg, ...style }, ...rest, children: [leading, children, trailing] }));
});
const PAD = {
    none: "",
    sm: "p-3",
    md: "p-4",
    lg: "p-5",
};
const RAD = {
    md: "rounded-[20px]",
    lg: "rounded-[24px]",
    xl: "rounded-[28px]",
};
export function PaperCard({ interactive, padding = "md", radius = "md", tint, className, style, children, ...rest }) {
    return (_jsx("div", { className: cx("paper-lift", interactive && "paper-lift-interactive cursor-pointer", PAD[padding], RAD[radius], className), style: tint ? { background: tint, ...style } : style, ...rest, children: children }));
}
export function Chip({ tint, icon, size = "md", className, style, children, ...rest }) {
    const h = size === "sm" ? "h-6 text-[11px] px-2.5" : "h-7 text-xs px-3";
    return (_jsxs("span", { className: cx("paper-lift-sm inline-flex items-center gap-1 rounded-full bg-white font-bold text-[var(--ink)] whitespace-nowrap", h, className), style: tint ? { background: tint, ...style } : style, ...rest, children: [icon, children] }));
}
export function NameTag({ name, color, className, style, animated = true }) {
    const c = color ?? pastelFor(name);
    return (_jsx("span", { className: cx("paper-lift-sm inline-flex items-center justify-center rounded-full px-4 font-black text-[var(--ink)]", "h-[34px] min-w-[132px] text-[14px] tracking-tight", animated && "motion-nametag-in", className), style: {
            background: c,
            /* Subtle character-tinted glow on the tag */
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 6px -2px ${c}aa, 0 1px 2px -1px rgba(60,40,30,0.12)`,
            ...style,
        }, children: name }));
}
/** Small "✦REMIX" badge — floats absolutely above the NameTag for
 *  player-authored dialogue beats. Italic serif gives the chip an ornate
 *  signature character that DM Sans (the default UI face) deliberately
 *  doesn't have. No glow / aura per design feedback. */
function RemixInlineBadge() {
    return (_jsx("span", { className: "select-none pointer-events-none inline-flex items-center", style: {
            background: "var(--paper)",
            border: "1px solid var(--ink)",
            borderRadius: "999px",
            padding: "1px 9px 2px",
            height: "20px",
            fontFamily: '"Playfair Display", "Bodoni 72", "Georgia", serif',
            fontSize: "12px",
            fontStyle: "italic",
            fontWeight: 900,
            letterSpacing: "0.14em",
            color: "var(--ink)",
            boxShadow: "0 1.5px 0 var(--ink)",
            whiteSpace: "nowrap",
            lineHeight: 1,
        }, children: "\u2726REMIX" }));
}
export function DialogueBubble({ kind, speaker, speakerColor, text, className, isTyping, fromRemix, }) {
    if (kind === "narrator") {
        return (_jsx("div", { className: cx("flex flex-col items-center motion-bubble-in", className), children: _jsxs("div", { className: "paper-stamp break-words rounded-[36px] px-7 py-4 text-center text-[18px] font-semibold leading-[1.42] tracking-tight text-[var(--ink)] relative", style: { maxWidth: "min(640px, 90vw)" }, children: [text, isTyping && _jsx(TypingCaret, {}), !isTyping && _jsx(ReadyChevron, {})] }) }));
    }
    if (kind === "thinking") {
        return (_jsxs("div", { className: cx("flex flex-col items-start motion-bubble-in", className), children: [_jsxs("div", { className: "-mb-2 ml-2 relative z-10", children: [fromRemix && (_jsx("div", { className: "pointer-events-none absolute -top-5 left-0 z-20", children: _jsx(RemixInlineBadge, {}) })), _jsx(ThoughtPill, { speaker: speaker })] }), _jsxs("div", { className: "paper-stamp break-words rounded-[44px] px-7 py-5 text-[17px] font-semibold leading-[1.45] tracking-tight text-[var(--ink)] relative italic", style: { maxWidth: "min(560px, 88vw)" }, children: [text, isTyping && _jsx(TypingCaret, {}), !isTyping && _jsx(ReadyChevron, {})] })] }));
    }
    // dialogue
    const align = kind === "dialogue-right" ? "items-end" : "items-start";
    const tint = speakerColor ?? (speaker ? pastelFor(speaker) : "#fca5a5");
    return (_jsxs("div", { className: cx("flex flex-col motion-bubble-in", align, className), children: [speaker && (
            // NameTag wrapper — `relative` so the remix badge can pin itself
            // absolutely above the tag (floating "ribbon" sitting on the speaker's
            // shoulder area, per design feedback — the inline chip read as a
            // suffix, the floating badge reads as an authorship mark).
            _jsxs("div", { className: "relative z-10", style: { marginBottom: "-8px" }, children: [_jsx(NameTag, { name: speaker, color: speakerColor }), fromRemix && (_jsx("div", { className: "pointer-events-none absolute -top-5 z-20" +
                            (kind === "dialogue-right" ? " right-0" : " left-0"), children: _jsx(RemixInlineBadge, {}) }))] })), _jsxs("div", { className: "paper-stamp break-words rounded-[22px] px-7 py-[18px] text-[17px] font-semibold leading-[1.45] tracking-tight text-[var(--ink)] relative", style: {
                    /* very subtle character-color wash on top 2px for visual connection */
                    backgroundImage: `linear-gradient(to bottom, ${tint}22 0, ${tint}00 18px), var(--paper-warm)`,
                    maxWidth: "min(520px, 85vw)",
                }, children: [text, isTyping && _jsx(TypingCaret, {}), !isTyping && _jsx(ReadyChevron, {})] })] }));
}
/** Typewriter caret — shown inline at the end of the revealing text.
 *  Blinks at a soft cadence so the reveal feels alive without being busy. */
function TypingCaret() {
    return (_jsx("span", { "aria-hidden": true, className: "motion-caret inline-block align-baseline ml-[1px] w-[2px] h-[1em] translate-y-[2px] bg-[var(--ink)]" }));
}
/** Ready-to-tap indicator — small chevron that fades in 420ms after bubble,
 *  then gently bobs to signal "ready". Positioned bottom-right inside bubble. */
function ReadyChevron() {
    return (_jsx("span", { "aria-hidden": true, className: "motion-chevron pointer-events-none absolute bottom-2 right-3 text-[12px] leading-none text-[var(--ink-muted)]", style: { filter: "drop-shadow(0 1px 0 rgba(255,255,255,0.6))" }, children: "\u25BE" }));
}
/**
 * ThoughtPill — pill-shaped speaker label for internal-monologue lines.
 *
 * Replaces the older asymmetric ThinkingCloud SVG, which never centered the
 * name reliably (its visual mass sat upper-right while the geometric center
 * fell on a flat empty area). The pill borrows NameTag's anatomy so text
 * centering is structural, then layers three "thought-bubble" signals so it
 * can never be mistaken for a spoken-dialogue NameTag:
 *
 *   1. White paper surface (vs NameTag's pastel character color)
 *   2. Italic Georgia serif text (vs DM Sans bold)
 *   3. 💭 leading glyph + three trailing puffs at the lower-left, getting
 *      smaller toward the tail (preserved from the original cloud SVG —
 *      the part that was actually doing the iconographic work)
 *
 * Slightly subordinate to NameTag (28px tall vs 34px) — internal voice is
 * quieter than speech.
 */
function ThoughtPill({ speaker }) {
    return (_jsxs("div", { className: "relative inline-block", children: [_jsxs("span", { className: "paper-lift-sm inline-flex items-center justify-center gap-1.5 rounded-full select-none", style: {
                    background: "var(--paper-warm)",
                    border: "1px solid rgba(60,40,30,0.18)",
                    height: "28px",
                    minWidth: "100px",
                    padding: "0 14px",
                    fontFamily: '"Georgia", "Cambria", serif',
                    fontSize: "12px",
                    fontStyle: "italic",
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                    color: "rgba(34, 32, 32, 0.82)",
                    whiteSpace: "nowrap",
                    lineHeight: 1,
                }, children: [_jsx("span", { "aria-hidden": true, style: { fontSize: "11px", lineHeight: 1 }, children: "\uD83D\uDCAD" }), speaker ?? ""] }), _jsx("span", { "aria-hidden": true, className: "absolute pointer-events-none rounded-full", style: {
                    width: "9px",
                    height: "9px",
                    bottom: "-3px",
                    left: "-3px",
                    background: "var(--paper-warm)",
                    border: "1px solid rgba(60,40,30,0.18)",
                    boxShadow: "0 1px 2px -1px rgba(60,40,30,0.10)",
                } }), _jsx("span", { "aria-hidden": true, className: "absolute pointer-events-none rounded-full", style: {
                    width: "5px",
                    height: "5px",
                    bottom: "-9px",
                    left: "-8px",
                    background: "var(--paper-warm)",
                    border: "1px solid rgba(60,40,30,0.18)",
                } }), _jsx("span", { "aria-hidden": true, className: "absolute pointer-events-none rounded-full", style: {
                    width: "3px",
                    height: "3px",
                    bottom: "-13px",
                    left: "-12px",
                    background: "var(--paper-warm)",
                    border: "1px solid rgba(60,40,30,0.18)",
                } })] }));
}
/** Thinking cloud SVG — kept for potential reuse but no longer wired to the
 *  thinking variant of DialogueBubble (replaced by ThoughtPill above). */
function ThinkingCloud({ className }) {
    return (_jsxs("svg", { viewBox: "0 0 130 49", className: cx("h-[44px] w-[118px]", className), fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M24 38 C10 38 4 30 4 22 C4 12 13 8 20 10 C22 4 30 1 40 3 C47 -1 60 1 64 9 C74 6 85 9 90 16 C103 15 112 22 112 30 C112 38 103 42 94 40 C90 46 80 47 72 44 C64 48 52 47 46 42 C38 46 29 45 24 38 Z", fill: "#fffaf6", stroke: "rgba(60,35,25,0.18)", strokeWidth: "1.4" }), _jsx("circle", { cx: "18", cy: "45", r: "3.6", fill: "#fffaf6", stroke: "rgba(60,35,25,0.18)", strokeWidth: "1.2" }), _jsx("circle", { cx: "9", cy: "48", r: "2.2", fill: "#fffaf6", stroke: "rgba(60,35,25,0.18)", strokeWidth: "1" })] }));
}
export function TabBar({ items, activeKey, onChange, className }) {
    return (_jsx("div", { className: cx("pointer-events-none fixed inset-x-0 z-40 flex justify-center", className), style: { bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }, children: _jsx("div", { className: "paper-lift pointer-events-auto flex items-center gap-2 rounded-[100px] bg-white p-1.5", children: items.map((it) => {
                const active = it.key === activeKey;
                return (_jsxs("button", { type: "button", onClick: () => onChange(it.key), className: cx("inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-sm font-bold tracking-tight", "transition-transform active:translate-y-[1px]", active ? "paper-lift-sm text-[var(--ink)]" : "text-[var(--ink-muted)] hover:text-[var(--ink)]"), style: active ? { background: it.accent } : undefined, children: [it.icon, it.label] }, it.key));
            }) }) }));
}
export function PhoneCard({ contact, subtitle = "iMessage", messages, placeholder = "iMessage", className, typing, footer, language, }) {
    return (_jsxs("div", { className: cx("w-[320px] overflow-hidden rounded-[24px] bg-[var(--paper-2)] p-5", className), style: { boxShadow: "0 18px 48px -16px rgba(0,0,0,0.6)" }, children: [_jsxs("div", { className: "mb-4 text-center", children: [_jsx("div", { className: "text-[18px] font-black text-[var(--ink)]", children: contact }), _jsx("div", { className: "mt-0.5 text-xs text-[var(--ink-muted)]", children: subtitle })] }), _jsxs("div", { className: "mb-4 flex flex-col gap-2", children: [messages.length === 0 && !typing && (_jsx("div", { className: "py-4 text-center text-[12px] font-semibold text-[var(--ink-muted)]", children: t(language, "player.phoneEmpty") })), messages.map((m, i) => (_jsx("div", { className: cx("max-w-[85%] px-4 py-2.5 text-[16px] font-medium leading-[1.3]", m.from === "them"
                            ? "self-start rounded-[18px] rounded-bl-[6px] bg-[var(--imsg-grey)] text-[var(--ink)]"
                            : "self-end rounded-[18px] rounded-br-[6px] bg-[var(--imsg-blue)] text-white"), children: m.text }, m.id ?? i))), typing && (_jsx("div", { className: "self-start rounded-[18px] rounded-bl-[6px] bg-[var(--imsg-grey)] px-4 py-2.5", children: _jsx(TypingDots, {}) }))] }), footer ?? (_jsx("div", { className: "flex h-9 items-center rounded-[20px] bg-white px-4 text-[13px] text-[var(--ink-muted)]", children: placeholder }))] }));
}
function TypingDots() {
    return (_jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx("span", { className: "h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--ink-muted)] [animation-delay:-0.3s]" }), _jsx("span", { className: "h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--ink-muted)] [animation-delay:-0.15s]" }), _jsx("span", { className: "h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--ink-muted)]" })] }));
}
export function Modal({ onDismiss, children, veil = "default", align = "center", className }) {
    const veilColor = veil === "soft" ? "var(--veil-soft)" : veil === "strong" ? "var(--veil-strong)" : "var(--veil)";
    return (_jsx("div", { onClick: onDismiss, className: cx("absolute inset-0 z-40 flex p-4", align === "center" ? "items-center justify-center" : "items-end justify-center pb-10", className), style: { background: veilColor }, children: _jsx("div", { onClick: (e) => e.stopPropagation(), children: children }) }));
}
export function IconPill({ size = "md", tint, className, style, children, ...rest }) {
    const s = size === "sm" ? "h-8 w-8" : "h-10 w-10";
    return (_jsx("button", { type: "button", className: cx("paper-lift-sm inline-flex items-center justify-center rounded-full bg-white", "active:translate-y-[1px]", s, className), style: tint ? { background: tint, ...style } : style, ...rest, children: children }));
}
export function LinkPill({ href, icon, tint, children, className }) {
    return (_jsxs("a", { href: href, className: cx("paper-lift-sm inline-flex h-8 items-center gap-1.5 rounded-full bg-white px-3 text-xs font-bold text-[var(--ink)]", className), style: tint ? { background: tint } : undefined, children: [icon, children] }));
}
